import api from './client'

export type BillingPlanCode = 'MONTHLY' | 'ANNUAL'
export type BillingStatus = 'trialing' | 'active' | 'past_due' | 'expired' | 'sample'

export interface Plan {
  code: BillingPlanCode
  kind: 'RECURRING'
  amountPaise: number
  /** Undiscounted "regular" price; equals `amountPaise` when there's no discount. */
  listAmountPaise: number
  currency: string
  interval: 'MONTH' | 'YEAR'
}

export interface Entitlement {
  status: BillingStatus
  entitled: boolean
  trialEndsAt: string | null
}

export interface BillingStatusDetail extends Entitlement {
  subscription: {
    planCode: BillingPlanCode
    status: string
    currentEnd: string | null
    cancelAtCycleEnd: boolean
  } | null
  payments: {
    amount: number
    currency: string
    method: string | null
    paidAt: string
    status: string
    receiptUrl: string | null
  }[]
}

export interface CheckoutResponse {
  razorpaySubscriptionId: string
  /** Paise. Server-decided — never sent by the client. */
  amount: number
  currency: string
  keyId: string
}

export interface VerifyResponse {
  verified: boolean
  razorpayPaymentId: string
}

/** Public — no auth, no household. Backs the homepage pricing section. */
export function fetchPlans(): Promise<Plan[]> {
  return api.get('/billing/plans').then((r) => r.data)
}

/** Lightweight entitlement — drives the trial-ended read-only mode and the trial banners. */
export function fetchEntitlement(): Promise<Entitlement> {
  return api.get('/billing/entitlement').then((r) => r.data)
}

/** Full billing view for the Settings -> Billing page. */
export function fetchBillingStatus(): Promise<BillingStatusDetail> {
  return api.get('/billing').then((r) => r.data)
}

/** Start checkout for a plan — creates a Razorpay subscription. Owner only. */
export function createCheckout(planCode: BillingPlanCode): Promise<CheckoutResponse> {
  return api.post('/billing/checkout', { planCode }).then((r) => r.data)
}

/** Confirm a completed Checkout payment. Throws (400) if the signature does not match. */
export function verifyCheckout(payload: {
  razorpaySubscriptionId: string
  razorpayPaymentId: string
  razorpaySignature: string
}): Promise<VerifyResponse> {
  return api.post('/billing/checkout/verify', payload).then((r) => r.data)
}

/** Cancel the recurring subscription — at period end (default) or immediately. Owner only. */
export function cancelSubscription(atCycleEnd = true): Promise<void> {
  return api.post('/billing/cancel', { atCycleEnd }).then(() => undefined)
}
